import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BODY_STYLES, YEARS, VehicleAPI } from '../mock';
import { useMakes } from '../context/SettingsContext';

const PRICES = [
  { v: '1000000', label: 'J$1M' },
  { v: '2000000', label: 'J$2M' },
  { v: '3000000', label: 'J$3M' },
  { v: '4000000', label: 'J$4M' },
  { v: '5000000', label: 'J$5M' },
  { v: '6000000', label: 'J$6M' },
  { v: '8000000', label: 'J$8M' },
  { v: '10000000', label: 'J$10M' }
];

export default function SearchFilter({ embedded = false }) {
  const navigate = useNavigate();
  const MAKES = useMakes();
  const [f, setF] = useState({ make: '', model: '', body: '', minYear: '', maxYear: '', minPrice: '', maxPrice: '' });
  const [count, setCount] = useState(0);
  const [allModels, setAllModels] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);

  useEffect(() => {
    VehicleAPI.list({ limit: 200 }).then(list => {
      setAllVehicles(list);
      setCount(list.length);
      setAllModels(Array.from(new Set(list.map(v => v.model))).sort());
    }).catch(() => {});
  }, []);

  const models = f.make ? Array.from(new Set(allVehicles.filter(v => v.make === f.make).map(v => v.model))) : allModels;

  const submit = () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
    navigate(`/inventory?${params.toString()}`);
  };
  const reset = () => setF({ make: '', model: '', body: '', minYear: '', maxYear: '', minPrice: '', maxPrice: '' });

  return (
    <section className={`${embedded ? '' : 'py-16 lg:py-24 bg-[#fafafa]'} relative`}>
      <div className={`${embedded ? '' : 'max-w-7xl mx-auto px-5 lg:px-8'}`}>
        {!embedded && (
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-white border border-black/10 px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold">
              <Sparkles size={12} className="text-[#e11d2c]" /> Smart Search
            </div>
            <h2 className="font-display mt-4 text-[34px] sm:text-[44px] font-black tracking-tight text-black">Find your perfect ride</h2>
            <p className="text-black/55 mt-3 max-w-xl mx-auto">Search through our inventory — every vehicle verified, inspected, and ready to roll.</p>
          </div>
        )}

        <div className="bg-white border border-black/10 rounded-2xl p-5 lg:p-7 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)]">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            <FieldSelect label="Make" value={f.make} onChange={(v) => setF({ ...f, make: v, model: '' })} options={MAKES} />
            <FieldSelect label="Model" value={f.model} onChange={(v) => setF({ ...f, model: v })} options={models} />
            <FieldSelect label="Body" value={f.body} onChange={(v) => setF({ ...f, body: v })} options={BODY_STYLES} />
            <FieldSelect label="Min Year" value={f.minYear} onChange={(v) => setF({ ...f, minYear: v })} options={YEARS.map(String)} />
            <FieldSelect label="Max Year" value={f.maxYear} onChange={(v) => setF({ ...f, maxYear: v })} options={YEARS.map(String)} />
            <FieldSelectV label="Min Price" value={f.minPrice} onChange={(v) => setF({ ...f, minPrice: v })} options={PRICES} />
            <FieldSelectV label="Max Price" value={f.maxPrice} onChange={(v) => setF({ ...f, maxPrice: v })} options={PRICES} />
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="text-[12px] text-black/50">
              <b className="text-black">{count}</b> vehicles in current inventory · Updated daily
            </div>
            <div className="flex gap-2">
              <Button onClick={reset} variant="outline" className="rounded-full border-black/15 hover:border-black hover:bg-black hover:text-white h-11 px-5 font-semibold">Reset</Button>
              <Button onClick={submit} className="btn-primary bg-[#e11d2c] hover:bg-[#c01526] text-white rounded-full h-11 px-7 font-semibold inline-flex items-center gap-2">
                <Search size={15} /> Search
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] tracking-[0.18em] uppercase font-semibold text-black/55 mb-1.5">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11 rounded-lg border-black/15 hover:border-black focus:ring-[#e11d2c] focus:border-[#e11d2c] text-[13px]">
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function FieldSelectV({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] tracking-[0.18em] uppercase font-semibold text-black/55 mb-1.5">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11 rounded-lg border-black/15 hover:border-black focus:ring-[#e11d2c] focus:border-[#e11d2c] text-[13px]">
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
